/**
 * @file     SeoHubShell.tsx
 * @purpose  Shared shell for the programmatic hub and explainer pages, so every
 *           family reads as one system instead of each hub carrying a
 *           hand-rolled layout. Built on the hub kit (src/components/hub):
 *           breadcrumb, eyebrow, display H1 and subhead over the hero wash, an
 *           optional aside that never leaves an empty column, the stat row,
 *           related links and the provenance block.
 */
'use client';

import { useId, useMemo, useState } from 'react';
import Link from 'next/link';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { hubLayoutTokens } from '@/design-system/tokens/layout';
import { hubTypographyTokens } from '@/design-system/tokens/typography';
import { Breadcrumb, toBreadcrumbItems } from '@/components/hub/Breadcrumb';
import { ComparisonTable } from '@/components/hub/ComparisonTable';
import { ProvenanceNote, type ProvenanceNoteProps } from '@/components/hub/ProvenanceNote';
import { RelatedLinks, type RelatedLinkItem } from '@/components/hub/RelatedLinks';
import { SectionHeader } from '@/components/hub/SectionHeader';
import { StatRow, type StatDeltaTone } from '@/components/hub/StatRow';
import { eyebrowSx, hairline, headingFont, hubColors } from '@/components/hub/hubKitStyles';
import type { DataProvenanceProps } from '@/features/seo/DataProvenance';
import { formatCadExact, formatPercent } from '@/utils/dataPresentation';
import { summarizeTrend } from '@/utils/statistics';

export interface SeoHubCrumb {
  label: string;
  href?: string;
}

export interface SeoHubStat {
  label: string;
  /** Null, empty, a dash or "Unavailable" drops the tile instead of printing a placeholder. */
  value: string | number | null | undefined;
  delta?: string | null;
  deltaTone?: StatDeltaTone;
}

export interface SeoHubShellProps {
  eyebrow: string;
  heading: string;
  intro: string;
  crumbs: SeoHubCrumb[];
  /** Hero stat row, two to four figures. Placeholder figures drop their tile. */
  stats?: SeoHubStat[];
  /** Named source for the stat row, e.g. "MLS® Reciprocity feed". */
  statsSource?: string;
  /** Last refresh of the stat figures, appended to the source line. */
  statsUpdatedAt?: string | Date | null;
  /**
   * Hero aside: a tool, a capture, a gate teaser, or nothing. Two columns on
   * desktop; under the subhead on mobile. An aside that renders no markup
   * collapses, so the hero never shows an empty column.
   */
  aside?: React.ReactNode;
  /** Hero buttons under the intro (one primary, at most one secondary). */
  actions?: React.ReactNode;
  /**
   * Contained assistant for the page family. It renders after the page
   * evidence, never in the hero, so it cannot displace a conversion aside.
   */
  assistant?: React.ReactNode;
  children: React.ReactNode;
  /** Cross-family links, rendered as a link matrix after the page evidence. */
  relatedLinks?: RelatedLinkItem[];
  /** Heading over the related links. */
  relatedLinksTitle?: string;
  /** Slot for a server-rendered external-data panel (this shell is a client
   *  component, so a Server Component cannot be constructed inside it). Only
   *  pass one where the data is editorially relevant to the hub. */
  dataPanel?: React.ReactNode;
  footnote?: string;
  /** Optional page-specific methodology note. Rendered in the provenance block
   *  beside the shared author and provenance lines; it never replaces them. */
  trustNote?: React.ReactNode;
  /** Data-provenance details for the provenance block. Defaults to the live
   *  MLS® source with no refresh timestamp. */
  provenance?: Pick<DataProvenanceProps, 'source' | 'feedProviders' | 'refreshedAt' | 'method' | 'mlsFootnote'> &
    Pick<ProvenanceNoteProps, 'methodHref' | 'methodLabel' | 'limits' | 'correctionSubject'>;
}

export function SeoHubShell({
  eyebrow,
  heading,
  intro,
  crumbs,
  stats,
  statsSource,
  statsUpdatedAt,
  aside,
  actions,
  assistant,
  children,
  relatedLinks,
  relatedLinksTitle = 'Related pages',
  dataPanel,
  footnote,
  trustNote,
  provenance,
}: SeoHubShellProps) {
  const h1 = hubTypographyTokens.h1;
  const subhead = hubTypographyTokens.subhead;
  // Only a deterministic utility or required lead flow belongs beside the
  // answer. Assistants are secondary and render after the page evidence.
  const heroAside = aside;
  // Beside an aside the figures sit under the subhead, inside the first screen;
  // without one they span the container under the hero copy.
  const statRow =
    stats && stats.length > 0 ? <StatRow items={stats} source={statsSource} updatedAt={statsUpdatedAt} /> : null;

  return (
    <Box sx={{ color: hubColors.textPrimary }}>
      <Box
        component="section"
        sx={{
          pt: hubLayoutTokens.gutter.fluid,
          pb: hubLayoutTokens.sectionPadding.fluid,
          borderBottom: hairline,
          background: `radial-gradient(ellipse 60% 100% at 20% 0%, ${hubColors.heroWash}, transparent 70%), ${hubColors.backgroundPrimary}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            data-hub-hero-grid
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
              },
              columnGap: { md: 5, lg: 8 },
              rowGap: { xs: 6, md: 5 },
              alignItems: 'start',
            }}
          >
            <Stack spacing={2} sx={{ minWidth: 0, maxWidth: 860 }}>
              <Breadcrumb items={toBreadcrumbItems(crumbs)} />
              <Box component="p" sx={{ ...eyebrowSx, pt: 1 }}>
                {eyebrow}
              </Box>
              <Box
                component="h1"
                sx={{
                  m: 0,
                  fontFamily: headingFont,
                  fontSize: h1.fluid,
                  fontWeight: h1.fontWeight,
                  lineHeight: h1.lineHeight,
                  letterSpacing: h1.letterSpacing,
                  textWrap: 'balance',
                  overflowWrap: 'break-word',
                }}
              >
                {heading}
              </Box>
              <Box
                component="p"
                sx={{
                  m: 0,
                  pt: 1,
                  maxWidth: subhead.maxWidth,
                  fontSize: subhead.fluid,
                  lineHeight: 1.45,
                  letterSpacing: '-0.01em',
                  color: hubColors.textSecondary,
                  textWrap: 'pretty',
                }}
              >
                {intro}
              </Box>
              {actions ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 2 }}>{actions}</Box>
              ) : null}
              {statRow && heroAside ? <Box sx={{ display: { xs: 'none', md: 'block' }, pt: 2 }}>{statRow}</Box> : null}
            </Stack>

            {heroAside ? (
              <Box data-hub-hero-aside sx={{ minWidth: 0, '&:empty': { display: 'none' } }}>
                {heroAside}
              </Box>
            ) : null}
          </Box>

          {statRow && !heroAside ? <Box sx={{ mt: { xs: 4, md: 6 } }}>{statRow}</Box> : null}
          {/* Phones put the aside straight under the subhead, then the figures. */}
          {statRow && heroAside ? <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 4 }}>{statRow}</Box> : null}
        </Container>
      </Box>

      <Box component="section" sx={{ py: hubLayoutTokens.sectionPadding.fluid, backgroundColor: hubColors.backgroundPrimary }}>
        <Container maxWidth="lg">
          {children}

          {dataPanel && <Box sx={{ mt: { xs: 5, md: 7 } }}>{dataPanel}</Box>}

          {assistant ? (
            <Box sx={{ mt: { xs: 7, md: 10 }, maxWidth: 760 }}>
              {assistant}
            </Box>
          ) : null}

          {relatedLinks && relatedLinks.length > 0 ? (
            <Box sx={{ mt: { xs: 7, md: 10 } }}>
              <RelatedLinks items={relatedLinks} title={relatedLinksTitle} titleVariant="label" />
            </Box>
          ) : null}

          <Box sx={{ mt: { xs: 6, md: 8 } }}>
            <ProvenanceNote
              source={provenance?.source}
              refreshedAt={provenance?.refreshedAt}
              feedProviders={provenance?.feedProviders}
              method={provenance?.method}
              mlsFootnote={provenance?.mlsFootnote}
              methodHref={provenance?.methodHref}
              methodLabel={provenance?.methodLabel}
              limits={provenance?.limits}
              correctionSubject={provenance?.correctionSubject ?? `Correction: ${heading}`}
              note={trustNote}
            />
          </Box>

          {footnote && (
            <Typography sx={{ mt: { xs: 3, md: 4 }, fontSize: 11.5, color: hubColors.textMuted, maxWidth: '80ch', lineHeight: 1.7 }}>
              {footnote}
            </Typography>
          )}
        </Container>
      </Box>
    </Box>
  );
}

export function SeoHubSection({
  heading,
  intro,
  children,
  id,
}: {
  heading: string;
  intro?: string;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        mt: { xs: 6, md: 9 },
        pt: { xs: 4, md: 5 },
        borderTop: hairline,
        scrollMarginTop: 96,
      }}
    >
      <Box sx={{ mb: 6 }}>
        <SectionHeader title={heading} description={intro} />
      </Box>
      {children}
    </Box>
  );
}

export function SeoHubNarrative({ lines }: { lines: string[] }) {
  const { theme } = useAppTheme();
  const c = theme.semanticColors;
  return (
    <Stack spacing={3} sx={{ maxWidth: '70ch' }}>
      {lines.map((line) => (
        <Typography key={line} sx={{ fontSize: 16, lineHeight: 1.75, color: c.textSecondary }}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

/** Small trend view. The caller must render the same values in a table. */
export function SeoTrendSparkline({
  points,
  label,
  valueFormat = 'number',
}: {
  points: (number | null)[];
  label: string;
  valueFormat?: 'number' | 'cad';
}) {
  const { theme } = useAppTheme();
  const gradientId = useId().replace(/:/g, '');
  const values = points.filter((point): point is number => point != null);
  const trend = summarizeTrend(values);
  if (!trend) return null;
  const min = trend.domainMin;
  const max = trend.domainMax;
  const flat = trend.direction === 'flat';
  const span = max - min;
  const width = 100;
  const height = 80;
  const path = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - ((value - min) / span) * (height * 0.72) - height * 0.14;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  const areaPath = `${path} L${width},${height} L0,${height} Z`;
  return (
    <Box sx={{ mb: { xs: 3, md: 4 } }}>
      <Box
        component="svg"
        role="img"
        aria-label={label}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        sx={{ width: '100%', height: { xs: 150, md: 210 }, display: 'block' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.semanticColors.interactivePrimary} stopOpacity="0.24" />
            <stop offset="100%" stopColor={theme.semanticColors.interactivePrimary} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[20, 40, 60, 80].map((y) => (
          <line key={y} x1="0" x2={width} y1={y} y2={y} stroke={theme.semanticColors.borderSubtle} strokeWidth="0.35" />
        ))}
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path
          d={path}
          fill="none"
          stroke={theme.semanticColors.interactivePrimary}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
        {values.map((value, index) => {
          const x = (index / (values.length - 1)) * width;
          const y = height - ((value - min) / span) * (height * 0.72) - height * 0.14;
          return <circle key={`${value}-${index}`} cx={x} cy={y} r="1.35" fill={theme.semanticColors.interactivePrimary} />;
        })}
      </Box>
      <Typography sx={{ mt: 1, fontSize: 13, color: theme.semanticColors.textMuted }}>
        {valueFormat === 'cad'
          ? `${formatCadExact(trend.first)} to ${formatCadExact(trend.last)}. ${flat ? 'Less than 1% net change.' : `${formatPercent(Math.abs(trend.percentChange))} net change.`}`
          : `${trend.first.toLocaleString('en-CA')} to ${trend.last.toLocaleString('en-CA')}. ${flat ? 'Less than 1% net change.' : `${formatPercent(Math.abs(trend.percentChange))} net change.`}`}
      </Typography>
    </Box>
  );
}

export function SeoDistributionBars({
  items,
  label,
}: {
  items: { label: string; value: number; primary: string; secondary?: string }[];
  label: string;
}) {
  const { theme } = useAppTheme();
  const c = theme.semanticColors;
  const max = Math.max(...items.map((item) => item.value), 1);

  return (
    <Box component="ul" aria-label={label} sx={{ p: 0, m: 0, listStyle: 'none' }}>
      {items.map((item) => (
        <Box
          component="li"
          key={item.label}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(96px, 0.8fr) minmax(110px, 1.4fr)', md: 'minmax(150px, 0.7fr) minmax(240px, 2fr) minmax(170px, 0.75fr)' },
            gap: { xs: 2, md: 3 },
            alignItems: 'center',
            minHeight: 58,
            borderBottom: `1px solid ${c.borderSubtle}`,
          }}
        >
          <Typography sx={{ fontSize: { xs: 14, md: 15 }, fontWeight: 600 }}>{item.label}</Typography>
          <Box sx={{ height: 9, borderRadius: `${theme.layout.radius.pill}px`, overflow: 'hidden', backgroundColor: c.backgroundSecondary }}>
            <Box
              aria-hidden
              sx={{
                width: `${Math.max((item.value / max) * 100, 1.5)}%`,
                height: '100%',
                borderRadius: 'inherit',
                backgroundColor: c.interactivePrimary,
              }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, mt: { xs: -1, md: 0 }, textAlign: { md: 'right' } }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item.primary}</Typography>
            {item.secondary ? <Typography sx={{ fontSize: 12.5, color: c.textMuted }}>{item.secondary}</Typography> : null}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function SeoHubDataGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'repeat(2, minmax(0, 1fr))' },
        gap: { xs: 3, md: 4 },
        '& > *': { minWidth: 0 },
      }}
    >
      {children}
    </Box>
  );
}

function ArrowIcon() {
  return (
    <SvgIcon fontSize="small" aria-hidden>
      <path d="m13.2 5.2 5.8 5.8-5.8 5.8-1.4-1.4 3.4-3.4H5v-2h10.2l-3.4-3.4 1.4-1.4Z" />
    </SvgIcon>
  );
}

export interface SeoHubComparisonRow {
  /** Makes the whole row a link (the first cell renders as the anchor). */
  href?: string;
  cells: React.ReactNode[];
}

/**
 * Wide multi-column comparison table for hubs that compare rows on several
 * numeric axes. Delegates to the kit ComparisonTable (sticky first column, own
 * horizontal scroll on mobile); kept as the hub-family entry point so existing
 * callers keep their API.
 */
export function SeoHubComparisonTable({
  columns,
  rows,
  caption,
  previewRowCount,
  locked = false,
  onUnlock,
  lockedRowLabel = 'See all rows',
}: {
  columns: string[];
  rows: SeoHubComparisonRow[];
  caption?: string;
  /** Keeps this many leading rows readable while the remaining rows are a
   *  presentation-only preview. Never use this as a security boundary. */
  previewRowCount?: number;
  locked?: boolean;
  onUnlock?: () => void;
  lockedRowLabel?: string;
}) {
  return (
    <ComparisonTable
      caption={caption ?? columns.join(', ')}
      captionVisible={Boolean(caption)}
      columns={columns}
      rows={rows}
      previewRowCount={previewRowCount}
      locked={locked}
      onUnlock={onUnlock}
      lockedRowLabel={lockedRowLabel}
    />
  );
}

/** Uniform card grid used by every hub for its city or entity lists. */
export function SeoHubCardGrid({
  items,
}: {
  items: { href: string; title: string; meta?: string; cta?: string }[];
}) {
  const { theme } = useAppTheme();
  const c = theme.semanticColors;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
        gap: { xs: 4, md: 6 },
      }}
    >
      {items.map((it) => (
        <Box
          key={it.href}
          component={Link}
          href={it.href}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: { xs: 4, md: 5 },
            borderRadius: `${theme.layout.radius.lg}px`,
            border: `1px solid ${c.borderSubtle}`,
            backgroundColor: c.surfaceCard,
            textDecoration: 'none',
            color: 'inherit',
            minHeight: 116,
            transition: 'border-color 180ms ease, background-color 180ms ease, transform 180ms ease',
            '&:hover': { borderColor: c.interactivePrimary, backgroundColor: c.surfaceElevated, transform: 'translateY(-2px)' },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
            '&:focus-visible': { outline: `2px solid ${c.interactivePrimary}`, outlineOffset: 2 },
          }}
        >
          <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{it.title}</Typography>
          {it.meta && <Typography sx={{ fontSize: 14, color: c.textMuted }}>{it.meta}</Typography>}
          {it.cta && (
            <Stack direction="row" sx={{ mt: 'auto', pt: 1, alignItems: 'center', justifyContent: 'space-between', gap: 1, color: c.interactivePrimary }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{it.cta}</Typography>
              <ArrowIcon />
            </Stack>
          )}
        </Box>
      ))}
    </Box>
  );
}

/** Searchable directory for large programmatic hub indexes. */
export function SeoHubDirectory({
  items,
  searchLabel,
  noResultsText,
  variant = 'cards',
}: {
  items: { href: string; title: string; meta?: string; cta?: string; keywords?: string[] }[];
  searchLabel: string;
  noResultsText: string;
  variant?: 'cards' | 'compact';
}) {
  const { theme } = useAppTheme();
  const c = theme.semanticColors;
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('en-CA');
    if (!normalized) return items;
    return items.filter((item) =>
      [item.title, item.meta ?? '', ...(item.keywords ?? [])]
        .join(' ')
        .toLocaleLowerCase('en-CA')
        .includes(normalized),
    );
  }, [items, query]);

  return (
    <Box>
      <TextField
        fullWidth
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        label={searchLabel}
        type="search"
        inputProps={{ autoComplete: 'off' }}
        sx={{ mb: 3, maxWidth: 520 }}
      />
      {visible.length > 0 ? (
        variant === 'compact' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {visible.map((item) => (
              <Box
                key={item.href}
                component={Link}
                href={item.href}
                sx={{
                  minHeight: 68,
                  px: 4,
                  py: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  borderRadius: `${theme.layout.radius.lg}px`,
                  border: `1px solid ${c.borderSubtle}`,
                  color: c.textPrimary,
                  backgroundColor: c.surfaceCard,
                  textDecoration: 'none',
                  transition: 'border-color 180ms ease, background-color 180ms ease',
                  '&:hover': { borderColor: c.interactivePrimary, backgroundColor: c.surfaceElevated },
                  '&:focus-visible': { outline: `2px solid ${c.interactivePrimary}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15.5, fontWeight: 600 }}>{item.title}</Typography>
                  {item.meta ? <Typography sx={{ mt: 0.25, fontSize: 12.5, color: c.textMuted }}>{item.meta}</Typography> : null}
                </Box>
                <Box sx={{ flexShrink: 0, color: c.interactivePrimary }}><ArrowIcon /></Box>
              </Box>
            ))}
          </Box>
        ) : (
          <SeoHubCardGrid items={visible} />
        )
      ) : (
        <Box
          role="status"
          sx={{
            p: 3,
            borderRadius: `${theme.layout.radius.lg}px`,
            border: `1px solid ${c.borderSubtle}`,
            backgroundColor: c.surfaceCard,
            color: c.textSecondary,
          }}
        >
          {noResultsText}
        </Box>
      )}
    </Box>
  );
}
